import { ReactText, useCallback, useState, memo, ChangeEvent } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography'
import { InputLabel, Select as MuiSelect, MenuItem } from '@mui/material'
import { InputEventHandlers } from 'utils/tracking/amplitude/WithFieldInteraction'
import { SEPARATOR_VALUE } from 'components/PathSelect'

export interface ValueTransformer<T, R = unknown> {
  selector?: keyof T
  extractor?: (raw: T) => R
}

type SelectProps<T, R> = {
  label: ReactText
  error?: string
  tooltip?: string
  clue?: string
  showTooltip?: boolean
  placeholder?: string
  onTooltip?: (isDisplayed: boolean) => void
  value?: string | T
  name?: string
  valueTransformer?: ValueTransformer<T, R>
  optionLabel: keyof T | ((option: T) => string)
  onChange?: (value?: R) => void
  options?: Array<T>
} & Omit<InputEventHandlers<T | null>, 'onChange'>

const useStyles = makeStyles((theme) => ({
  container: {
    color: theme.palette.grey[900],
    height: '48px'
  },
  labelStyle: {
    fontSize: '14px',
    color: theme.palette.grey[900],
    marginBottom: '8px'
  },
  paperContainer: {
    maxHeight: '290px'
  },
  placeholderStyle: {
    color: theme.palette.grey[500]
  },
  errorStyle: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}))

const Select = <T, R>({
  label,
  error,
  options = [],
  onChange,
  onBlur,
  onFocus,
  name,
  valueTransformer = {},
  placeholder,
  value
}: SelectProps<T, R>) => {
  const {
    container,
    labelStyle,
    paperContainer,
    placeholderStyle,
    errorStyle
  } = useStyles()

  const { extractor } = valueTransformer

  const [selectedValue, setSelectedValue] = useState<T | null>(null)

  const changeHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: ChangeEvent<{ value: any }>) => {
      if (event.target.value) {
        setSelectedValue(event.target.value)
      }

      if (!onChange) {
        return
      }

      onChange(event.target.value)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, extractor, setSelectedValue]
  )

  const blurHandler = useCallback(() => {
    if (onBlur) {
      onBlur(selectedValue)
    }
  }, [onBlur, selectedValue])

  const focusHandler = useCallback(() => {
    if (onFocus) {
      onFocus(selectedValue)
    }
  }, [onFocus, selectedValue])

  const inputId =
    label && `text-input-${label.toString().toLowerCase().replace(/\s/g, '')}`

  const shouldDisplayPlaceholder = !!(!value && placeholder)

  return (
    <>
      <InputLabel htmlFor={inputId || ''} classes={{ root: labelStyle }}>
        {label}
      </InputLabel>
      <MuiSelect
        error={!!error}
        className={container}
        fullWidth
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={changeHandler as any} // Material UI types are not cool here :/
        onBlur={blurHandler}
        onFocus={focusHandler}
        id={name}
        value={value || selectedValue}
        variant="outlined"
        color="primary"
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
          autoFocus: false,
          classes: { paper: paperContainer }
        }}
        placeholder={placeholder}
        displayEmpty={shouldDisplayPlaceholder}
        {...(shouldDisplayPlaceholder && {
          renderValue: () => (
            <span className={placeholderStyle}>{placeholder}</span>
          )
        })}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {options?.map((option: any) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={String(option.value as T) === SEPARATOR_VALUE}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {!!error && (
        <Typography variant="caption" className={errorStyle}>
          {error}
        </Typography>
      )}
    </>
  )
}

export default memo(
  Select,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
) as typeof Select
